import useSWR from "swr";

import { IS_ADMIN, KEYSTONE, POLICY_ID, USER, isSSData } from "./ss-data-store";
import {
  BadRequestError,
  type SSError,
  UnauthorizedRequestError,
  isError,
} from "./ss-error";

/**
 * Access Keystone API Client.
 */
export const useKeystone = () => {
  return KEYSTONE;
};

/**
 * Read the user, set server-side.
 */
export function useUser() {
  return USER;
}

/**
 * Read the selected policy GUID, always set server-side.
 */
export function usePolicyId() {
  return POLICY_ID;
}

/**
 * Return platform id for a policy. Pulls from currently selected policy by default.
 */
export function usePolicyPlatformId(policyId = POLICY_ID) {
  const { data } = usePoliciesList();
  return data?.find((policy) => policy.keystonePolicyId === policyId)
    ?.platformPolicyId;
}

/**
 * Return selected policy.
 */
export function useSelectedPolicy(policyId = POLICY_ID) {
  const res = usePoliciesList();
  return {
    ...res,
    data: res.data?.find((policy) => policy.keystonePolicyId === policyId),
  };
}

/**
 * Get current UI events.
 */
export function useUiEvents() {
  const user = useUser();
  const fetcher = async () => {
    if (!user) {
      throw new UnauthorizedRequestError("no authenticated user");
    }
    const res = await KEYSTONE.getUiEvents(user.userId);
    if (isError(res)) {
      throw res;
    }
    return res;
  };
  return useSWR("uievents", fetcher);
}

/**
 * Latest list of policies.
 */
export function usePoliciesList() {
  const user = useUser();
  const fetcher = async () => {
    if (!user) {
      throw new UnauthorizedRequestError("no authenticated user");
    }
    const res = await KEYSTONE.getUser(user.email);
    if (isError(res)) {
      throw res;
    }
    // ADMIN HACK: if user is admin and policy is in server data append missing policy
    if (
      IS_ADMIN &&
      isSSData(window.SS_DATA) &&
      !res.policyList.find((p) => p.keystonePolicyId === POLICY_ID)
    ) {
      const adminPolicy = window.SS_DATA.policies?.find(
        (p) => p.keystonePolicyId === POLICY_ID,
      );
      if (adminPolicy) {
        return [...res.policyList, adminPolicy];
      }
    }
    return res.policyList;
  };
  return useSWR("policies", fetcher);
}

/**
 * Get the full policy from keystone.
 */
export function usePolicyDetail(policyId = POLICY_ID) {
  const fetcher = () => {
    if (!policyId) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getPolicy(policyId));
  };
  return useSWR(["policies", policyId], fetcher);
}

/**
 * Get payment plans and easypay fee for current policy
 */
export function usePaymentPlans() {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getPaymentPlans(POLICY_ID));
  };
  return useSWR(["policies", POLICY_ID, "payment-plans"], fetcher);
}

/**
 * Constants for insured phone number and other info
 */
export function useClaimsConstants(policyId = POLICY_ID) {
  const fetcher = () => {
    if (!policyId) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getClaimConstants(policyId));
  };
  return useSWR(["policies", policyId, "claims-constants"], fetcher);
}

/**
 * Get a users latest claims data.
 */
export function useClaimsList(policyId = POLICY_ID) {
  const fetcher = () => {
    if (!policyId) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getClaimsList(policyId));
  };
  return useSWR(["claims", policyId], fetcher, {
    refreshInterval(list) {
      if (
        list?.ClaimsTrackerInfo?.length &&
        list?.ClaimsTrackerInfo?.some(
          (claim) => claim.CarrierClaim?.Number === "PENDING",
        )
      ) {
        // Check if claims are still pending every 2 seconds
        return 2000;
      }
      return 0;
    },
  });
}

/**
 * Get a policy's offers data.
 */
export function usePolicyOffers() {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getPolicyOffers(POLICY_ID));
  };
  return useSWR("offers", fetcher);
}

/**
 * Get a policy's latest inspections data.
 */
export function useInspectionsList(inspectionsProofUploadFlag?: boolean) {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(
      KEYSTONE.getPolicyInspections(POLICY_ID, { inspectionsProofUploadFlag }),
    );
  };
  return useSWR("inspections", fetcher);
}

/**
 *
 */
export function useDocumentsToSign() {
  const fetcher = () => {
    if (!POLICY_ID) {
      throw new BadRequestError("no policy ID selected");
    }
    return throwSSError(KEYSTONE.getDocumentsToSign(POLICY_ID));
  };
  return useSWR(["policies", POLICY_ID, "documents", "status"], fetcher);
}

/**
 * Get a signing link for a document by envelope ID. Used for embedded Docusign.
 */
export function useDocumentSigningUrl(envelopeId: string) {
  const fetcher = () => {
    if (!envelopeId) {
      throw new BadRequestError("no envelope ID provided");
    }
    return throwSSError(KEYSTONE.getDocumentSigningUrl(envelopeId));
  };
  return useSWR(["documents", "signinglink", envelopeId], fetcher);
}

/**
 * Decode data using keystone api. Fetch is skipped if input is falsy.
 */
export function useDecodeData(input: string) {
  const fetcher = () => {
    return throwSSError(KEYSTONE.decodeData(input));
  };
  return useSWR(input ? ["decoded-data", input] : null, fetcher);
}

/**
 * Get eligible coverages for a policy.
 */
export function useEligibleCoverages(policyId = POLICY_ID) {
  const platformId = usePolicyPlatformId(policyId);
  const fetcher = () => {
    if (!platformId) {
      throw new BadRequestError("no policy id provided");
    }
    return throwSSError(KEYSTONE.eligibleCoverages(platformId));
  };
  return useSWR(["policies", policyId, "eligible-coverages"], fetcher);
}

/**
 * Get preview of coverage price
 */
export function useCoveragePreview(
  coverageName: string,
  protectionValue: number,
  policyId = POLICY_ID,
) {
  const platformId = usePolicyPlatformId(policyId);
  const fetcher = () => {
    if (!platformId) {
      throw new BadRequestError("no policy id provided");
    }
    return throwSSError(
      KEYSTONE.coveragePreview(platformId, coverageName, protectionValue),
    );
  };
  return useSWR(
    ["policies", policyId, "coverage-preview", coverageName],
    fetcher,
  );
}

async function throwSSError<T>(apiCall: Promise<T | SSError>) {
  const res = await apiCall;
  if (isError(res)) {
    throw res;
  }
  return res;
}
